import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft,
  FolderKanban,
  Users,
  Building2,
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Calendar,
  Target
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProjectDashboardPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [consultantStats, setConsultantStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectRes, dashboardRes, consultantsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/dashboard`),
        axios.get(`${API}/projects/${projectId}/consultants`)
      ]);
      setProject(projectRes.data);
      setDashboard(dashboardRes.data);
      setConsultantStats(consultantsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  if (!project || !dashboard) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Projet non trouvé</p>
          <Button asChild className="mt-4">
            <Link to="/admin/projects">Retour aux projets</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/projects">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Projets
            </Link>
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <FolderKanban className="w-8 h-8 text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                {project.name}
              </h1>
              <p className="text-slate-500">{project.description}</p>
            </div>
          </div>
          <Button asChild>
            <Link to={`/admin/projects/${projectId}/assignments`}>
              <Users className="w-4 h-4 mr-2" />
              Gérer les affectations
            </Link>
          </Button>
        </div>

        {/* Alerts */}
        {dashboard.alerts.length > 0 && (
          <div className="space-y-2">
            {dashboard.alerts.map((alert, idx) => (
              <Card key={idx} className={`border-l-4 ${
                alert.severity === 'high' ? 'border-l-red-500 bg-red-50' : 'border-l-orange-500 bg-orange-50'
              }`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertTriangle className={`w-5 h-5 ${
                    alert.severity === 'high' ? 'text-red-500' : 'text-orange-500'
                  }`} />
                  <span className={alert.severity === 'high' ? 'text-red-800' : 'text-orange-800'}>
                    {alert.message}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{dashboard.total_consultants}</p>
              <p className="text-xs text-slate-500">Consultants</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Building2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{dashboard.total_smes}</p>
              <p className="text-xs text-slate-500">PME</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{dashboard.total_assignments}</p>
              <p className="text-xs text-slate-500">Affectations</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 text-slate-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{dashboard.total_interventions}</p>
              <p className="text-xs text-slate-500">Interventions</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{dashboard.validated_interventions}</p>
              <p className="text-xs text-slate-500">Validées</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-orange-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{dashboard.validated_hours.toFixed(1)}h</p>
              <p className="text-xs text-slate-500">Heures validées</p>
            </CardContent>
          </Card>
        </div>

        {/* Progress Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Overall Progress */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Progression globale
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Avancement</span>
                  <span className="font-medium">{dashboard.overall_progress_percent.toFixed(1)}%</span>
                </div>
                <Progress value={dashboard.overall_progress_percent} className="h-3" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Taux de validation</span>
                  <span className="font-medium">{dashboard.validation_rate_percent.toFixed(1)}%</span>
                </div>
                <Progress value={dashboard.validation_rate_percent} className="h-3" />
              </div>
            </CardContent>
          </Card>

          {/* Time Progress */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Calendrier
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Temps écoulé</span>
                  <span className="font-medium">{dashboard.time_elapsed_percent.toFixed(1)}%</span>
                </div>
                <Progress value={dashboard.time_elapsed_percent} className="h-3" />
              </div>
              <div className="grid grid-cols-3 gap-4 pt-2">
                <div className="text-center">
                  <p className="text-lg font-bold text-slate-900">{dashboard.days_elapsed}</p>
                  <p className="text-xs text-slate-500">Jours écoulés</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-slate-900">{dashboard.days_remaining}</p>
                  <p className="text-xs text-slate-500">Jours restants</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-slate-900">{dashboard.total_days}</p>
                  <p className="text-xs text-slate-500">Total jours</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Consultants Table */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Progression par consultant</CardTitle>
            <CardDescription>Détail des performances de chaque consultant</CardDescription>
          </CardHeader>
          <CardContent>
            {consultantStats.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                Aucune affectation pour ce projet
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Consultant</TableHead>
                    <TableHead className="text-center">Affectations</TableHead>
                    <TableHead className="text-center">Interventions</TableHead>
                    <TableHead className="text-center">Heures validées</TableHead>
                    <TableHead>Progression</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {consultantStats.map((stat) => (
                    <TableRow key={stat.consultant_id}>
                      <TableCell className="font-medium">{stat.consultant_name}</TableCell>
                      <TableCell className="text-center">{stat.assignments.length}</TableCell>
                      <TableCell className="text-center">
                        <span className="text-emerald-600 font-medium">{stat.validated_interventions}</span>
                        <span className="text-slate-400"> / {stat.total_interventions}</span>
                      </TableCell>
                      <TableCell className="text-center">{stat.validated_hours.toFixed(1)}h</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={stat.overall_progress_percent} className="h-2 flex-1" />
                          <span className="text-sm font-medium w-12">
                            {stat.overall_progress_percent.toFixed(0)}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
